/**
 * @module
 *
 * @todo
 *
 *   - Implement Validation in sessionStore
 */

/**
 * @import {UserProfileData} from '../user/model/user-profile.js'
 * @import {ErrorResponse} from '../core/service/backend.js';
 */
import {service} from 'nova';
import {CONST, ENUM, guard, OBJECT, OR, RECORD, STRING} from '@acng/frontend-rubicon';
import {USER_PROFILE_DATA} from '../user/model/user-profile.js';
import {get, post, put} from '../core/service/backend.js';
import {sessionStore, USER_PROFILE} from './browser-storage.js';
import {undefined} from '@acng/frontend-bounty/std/value.js';
import {user} from 'acng/user/user.js';

class UserProfileApi {
  /**
   * @returns {Promise<UserProfileData>}
   */
  async get() {
    const data = await get('userProfile/view');
    ASSERT: guard(data, USER_PROFILE_DATA);

    return data;
  }

  /**
   * @param {Partial<UserProfileData>} partialData
   *
   * @returns {Promise<UserProfileData>}
   */
  async put(partialData) {
    const data = await put('userProfile/update', partialData);
    ASSERT: guard(data, USER_PROFILE_DATA);

    return data;
  }
}

@service
class CachedUserProfileApi extends UserProfileApi {
  // DEBUG = true;

  /**
   * @type {Promise<UserProfileData> | undefined}
   */
  #latestResult;

  /**
   * @type {UserProfileData | undefined}
   */
  #profile;

  /**
   * Flush all caches so that the next {@link CachedUserProfileApi.get} call will
   * perform an ACNG API request.
   */
  flush() {
    delete sessionStore[USER_PROFILE];
    this.#latestResult = undefined;
    this.#profile = undefined;
  }

  /**
   * 1. Use data from session store.
   * 2. If not available, use data from latest result.
   * 3. If not available, fetch data from ACNG API.
   * 4. Store data in session.
   *
   * The data is {@link guard | asserted} to be valid.
   *
   * @returns {Promise<UserProfileData>}
   *
   * @throws
   *
   *   - {@link ErrorResponse} When the user is not authenticated
   *
   * @override
   */
  async get() {
    let value = this.#profile;

    if (value) {
      return value;
    }

    value = sessionStore[USER_PROFILE];

    // TODO: Use watcher
    if (value && user.id != value.id) {
      this.flush();
      value = undefined;
    }

    if (value) {
      DEBUG: if ('DEBUG' in this && value)
        console.info('%cUse user profile from session store.', CONSOLE_STYLE, value);
      ASSERT: if (value) guard(value, USER_PROFILE_DATA);
      this.#profile = value;

      return value;
    }

    const fetching = this.#latestResult;

    if (!fetching) {
      const res = super.get();
      DEBUG: if ('DEBUG' in this) console.log('%cFetch user profile from ACNG API...', CONSOLE_STYLE, res);

      this.#latestResult = res;
      value = await res;

      if (this.#latestResult !== res) {
        return this.get();
      }

      sessionStore[USER_PROFILE] = value;

      DEBUG: if ('DEBUG' in this) console.info('%c... Got user profile from ACNG API.', CONSOLE_STYLE, value);
    } else {
      const data = await fetching;
      value = data;
    }

    this.#profile = value;

    return value;
  }

  /**
   * 1. Await previous fetch completion.
   * 2. Put data to ACNG API.
   * 3. Store updated profile in session.
   *
   * The data returned from the ACNG API is {@link guard | asserted} to be valid.
   *
   * @param {Partial<UserProfileData>} partialData
   *
   * @returns {Promise<UserProfileData>}
   *
   * @override
   */
  async put(partialData) {
    await this.#latestResult;

    const res = super.put(partialData);
    this.#latestResult = res;

    const data = await res;

    // Update caches!!
    this.#profile = data;
    sessionStore[USER_PROFILE] = data;

    return data;
  }
}

class UserProfileImageApi {
  async get() {
    const data = await get('userProfile/waiting-images');
    ASSERT: guard(data, asWaitingImage);

    return data;
  }

  /**
   * @param {FormData} formData
   * @param {Element} [initiator]
   *
   * @returns {Promise<string>} Temporary identifier for the uploaded image
   */
  async post(formData, initiator) {
    const data = await post('userProfile/image', formData, initiator);
    ASSERT: guard(data, asImageUploadResponse);

    return data.name;
  }
}

class UserProfileImageCropApi {
  async get() {
    //
  }

  async put() {
    //
  }
}

const asImageUploadResponse = /* @__PURE__ */ OBJECT({name: STRING});

const asWaitingImage = /* @__PURE__ */ (() =>
  OR(
    CONST(false),
    OBJECT({
      normal: RECORD(ENUM(/** @type {const} */ (['60', '120', '200', '320'])), STRING),
      wide: RECORD(ENUM(/** @type {const} */ (['160', '480', '900'])), STRING),
    }),
  ))();

const CONSOLE_STYLE = 'background: darkgreen; color: lightgreen; padding: 0 1pc; border-radius: 1pt';

export {CachedUserProfileApi as UserProfileApi, UserProfileImageApi, UserProfileImageCropApi};
